/**
 * Reader page state derivation (AP-778 §3) — a pure function of the URL
 * selection, the fetch, and the readiness gates, so the state machine itself
 * is unit-testable without mounting the page.
 *
 *   empty   — no study selected; landing content (recents/pending).
 *   loading — the selected study is not the revealed study: opaque
 *             content-area overlay until it is 100% ready. Covers BOTH the
 *             cold load and the study switch — deliberately one state; the
 *             reveal latch (revealedUuid === null ⇔ cold) already
 *             distinguishes them if treatment ever needs to diverge.
 *   active  — the selected study is revealed; the nominal working state.
 *   error   — the selected study CANNOT be shown: the fetch failed and there
 *             is no data. A transient failure (e.g. a background revalidation
 *             erroring while cached data is still present) never replaces a
 *             loaded study with the error canvas.
 */
export type ReaderState = 'empty' | 'loading' | 'active' | 'error';

export interface DeriveReaderStateArgs {
  selectedUuid: string | undefined;
  /** The selected study's payload is present (RTK keys data by uuid). */
  hasStudyData: boolean;
  /** The selected study's query reports an error. */
  hasFetchError: boolean;
  /** Reveal latch: the study the page last committed to showing. */
  revealedUuid: string | null;
  /** Every required readiness gate reports true (studyReadiness.tsx). */
  gatesReady: boolean;
}

export function deriveReaderState({
  selectedUuid,
  hasStudyData,
  hasFetchError,
  revealedUuid,
  gatesReady,
}: DeriveReaderStateArgs): ReaderState {
  if (!selectedUuid) return 'empty';
  // Error only when there is nothing to show — with data present the error is
  // transient (failed revalidation) and the study keeps rendering.
  if (hasFetchError && !hasStudyData) return 'error';
  if (revealedUuid === selectedUuid || (hasStudyData && gatesReady))
    return 'active';
  return 'loading';
}
